import {
  getFirestore,
  doc,
  getDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  orderBy,
  limit as firestoreLimit
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import Database from '../types/Database'

// --- Partner info type ---

export interface PartnerInfo {
  userId: string
  displayName: string
  handle: string | null
}

// --- Error types ---

export type InviteErrorCode =
  | 'AUTH_REQUIRED'
  | 'NOT_HOST'
  | 'DIVE_NOT_OPEN'
  | 'SELF_INVITE'
  | 'DUPLICATE'
  | 'HANDLE_NOT_FOUND'

// --- Result types ---

export type CreateInviteResult =
  | { success: true; inviteId: string }
  | { success: false; error: InviteErrorCode }

// --- Service functions ---

/**
 * Checks whether an active invite already exists for the given dive and invitee.
 * Uses inviteeUserId when non-null, falls back to inviteeEmail.
 * inviterId is required to satisfy Firestore read rules on the invites collection.
 */
export async function checkDuplicate(
  diveId: string,
  inviteeUserId: string | null,
  inviteeEmail: string | null,
  inviterId: string
): Promise<boolean> {
  const db = getFirestore()
  const invitesRef = collection(db, 'invites')

  if (inviteeUserId) {
    const q = query(
      invitesRef,
      where('inviterId', '==', inviterId),
      where('diveId', '==', diveId),
      where('inviteeUserId', '==', inviteeUserId),
      where('status', '==', 'active'),
      firestoreLimit(1)
    )
    const snap = await getDocs(q)
    return !snap.empty
  }

  if (inviteeEmail) {
    const q = query(
      invitesRef,
      where('inviterId', '==', inviterId),
      where('diveId', '==', diveId),
      where('inviteeEmail', '==', inviteeEmail),
      where('status', '==', 'active'),
      firestoreLimit(1)
    )
    const snap = await getDocs(q)
    return !snap.empty
  }

  return false
}

/**
 * Creates an invite for a dive. Validates authorization (authenticated, is host,
 * dive is open, not self-invite), checks for duplicates, resolves handle to userId
 * if needed, and creates the Invite_Document with correct field mapping.
 */
export async function createInvite(
  diveId: string,
  inviteeData: { handle?: string; email?: string; userId?: string }
): Promise<CreateInviteResult> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  // 1. Auth check
  if (!currentUser) {
    return { success: false, error: 'AUTH_REQUIRED' }
  }

  const db = getFirestore()

  // 2. Fetch dive document and verify host + status
  const diveRef = doc(db, 'dives', diveId)
  const diveSnap = await getDoc(diveRef)

  if (!diveSnap.exists()) {
    return { success: false, error: 'NOT_HOST' }
  }

  const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

  if (dive.hostId !== currentUser.uid) {
    return { success: false, error: 'NOT_HOST' }
  }

  if (dive.status !== 'open') {
    return { success: false, error: 'DIVE_NOT_OPEN' }
  }

  // 3. Resolve invitee identity
  let inviteeHandle: string | null = null
  let inviteeEmail: string | null = null
  let inviteeUserId: string | null = null

  if (inviteeData.handle) {
    inviteeHandle = inviteeData.handle

    if (inviteeData.userId) {
      // userId already resolved by HandleSearch — skip the handles collection lookup
      inviteeUserId = inviteeData.userId
    } else {
      // Handle-based invite: resolve userId from handles collection
      const handleRef = doc(db, 'handles', inviteeData.handle)
      const handleSnap = await getDoc(handleRef)

      if (!handleSnap.exists()) {
        return { success: false, error: 'HANDLE_NOT_FOUND' }
      }

      const handleData = handleSnap.data() as { uid: string }
      inviteeUserId = handleData.uid
    }
  } else if (inviteeData.email) {
    // Email-based invite: try to resolve userId from users collection
    inviteeEmail = inviteeData.email

    if (inviteeData.userId) {
      inviteeUserId = inviteeData.userId
    } else {
      // Try to find a user with this email
      const usersRef = collection(db, 'users')
      const emailQuery = query(
        usersRef,
        where('email', '==', inviteeData.email),
        firestoreLimit(1)
      )
      const emailSnap = await getDocs(emailQuery)

      if (!emailSnap.empty) {
        inviteeUserId = emailSnap.docs[0].id
      }
    }
  } else if (inviteeData.userId) {
    // Direct userId invite (from previous partners)
    inviteeUserId = inviteeData.userId

    // Resolve handle from users collection for display
    const userRef = doc(db, 'users', inviteeData.userId)
    const userSnap = await getDoc(userRef)

    if (userSnap.exists()) {
      const userData = userSnap.data() as Omit<Database.User, 'id'>
      inviteeHandle = userData.handle
    }
  }

  // 4. Self-invite check
  if (inviteeUserId && inviteeUserId === currentUser.uid) {
    return { success: false, error: 'SELF_INVITE' }
  }

  // 5. Duplicate check
  const isDuplicate = await checkDuplicate(diveId, inviteeUserId, inviteeEmail, currentUser.uid)
  if (isDuplicate) {
    return { success: false, error: 'DUPLICATE' }
  }

  // 6. Create Invite_Document
  const invitesRef = collection(db, 'invites')
  const inviteDoc = {
    diveId,
    inviterId: currentUser.uid,
    inviteeHandle,
    inviteeEmail,
    inviteeUserId,
    status: 'active' as const,
    createdAt: serverTimestamp()
  }

  const docRef = await addDoc(invitesRef, inviteDoc)

  return { success: true, inviteId: docRef.id }
}

/**
 * Fetches all active invites for the given user, ordered by createdAt descending.
 */
export async function getInvitesForUser(
  userId: string
): Promise<Database.Invite[]> {
  const db = getFirestore()
  const invitesRef = collection(db, 'invites')

  const q = query(
    invitesRef,
    where('inviteeUserId', '==', userId),
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc')
  )

  const snap = await getDocs(q)

  return snap.docs.map(d => ({
    id: d.id,
    ...(d.data() as Omit<Database.Invite, 'id'>)
  }))
}

/**
 * Returns previous dive partners for the host, excluding the host and
 * any users already invited to the given dive.
 *
 * A "previous partner" is someone who participated in a completed dive
 * where the host was also involved (as host or as approved participant).
 */
export async function getPreviousPartners(
  hostUid: string,
  diveId: string
): Promise<PartnerInfo[]> {
  const db = getFirestore()

  // 1. Find completed dives hosted by this user
  const divesRef = collection(db, 'dives')
  const hostedDivesQuery = query(
    divesRef,
    where('hostId', '==', hostUid),
    where('status', '==', 'completed')
  )
  const hostedDivesSnap = await getDocs(hostedDivesQuery)
  const hostedDiveIds = hostedDivesSnap.docs.map(d => d.id)

  // 2. Find completed dives where host was an approved participant
  const joinRequestsRef = collection(db, 'joinRequests')
  const hostParticipationQuery = query(
    joinRequestsRef,
    where('requesterId', '==', hostUid),
    where('status', '==', 'approved')
  )
  const hostParticipationSnap = await getDocs(hostParticipationQuery)

  // Filter to only completed dives
  const participatedDiveIds: string[] = []
  for (const reqDoc of hostParticipationSnap.docs) {
    const reqData = reqDoc.data()
    const diveRef = doc(db, 'dives', reqData.diveId)
    const diveSnap = await getDoc(diveRef)
    if (diveSnap.exists()) {
      const diveData = diveSnap.data() as Omit<Database.Dive, 'id'>
      if (diveData.status === 'completed') {
        participatedDiveIds.push(reqData.diveId)
      }
    }
  }

  // 3. Combine all completed dive IDs where host was involved
  const allCompletedDiveIds = [
    ...new Set([...hostedDiveIds, ...participatedDiveIds])
  ]

  if (allCompletedDiveIds.length === 0) {
    return []
  }

  // 4. Find all approved join requests for those dives to get participant UIDs
  const partnerUids = new Set<string>()

  // Firestore 'in' queries are limited to 30 values, so batch them
  const batchSize = 30
  for (let i = 0; i < allCompletedDiveIds.length; i += batchSize) {
    const batch = allCompletedDiveIds.slice(i, i + batchSize)
    const participantsQuery = query(
      joinRequestsRef,
      where('diveId', 'in', batch),
      where('status', '==', 'approved')
    )
    const participantsSnap = await getDocs(participantsQuery)

    for (const pDoc of participantsSnap.docs) {
      const pData = pDoc.data()
      partnerUids.add(pData.requesterId)
    }
  }

  // Also add the hosts of dives where the user was a participant
  for (const participatedDiveId of participatedDiveIds) {
    const participatedDiveRef = doc(db, 'dives', participatedDiveId)
    const participatedDiveSnap = await getDoc(participatedDiveRef)
    if (participatedDiveSnap.exists()) {
      const participatedDiveData = participatedDiveSnap.data() as Omit<
        Database.Dive,
        'id'
      >
      partnerUids.add(participatedDiveData.hostId)
    }
  }

  // 5. Exclude the host's own UID
  partnerUids.delete(hostUid)

  if (partnerUids.size === 0) {
    return []
  }

  // 6. Exclude users who already have an active invite for this dive
  const invitesRef = collection(db, 'invites')
  const activeInvitesQuery = query(
    invitesRef,
    where('diveId', '==', diveId),
    where('status', '==', 'active')
  )
  const activeInvitesSnap = await getDocs(activeInvitesQuery)
  const alreadyInvitedUids = new Set<string>()
  for (const invDoc of activeInvitesSnap.docs) {
    const invData = invDoc.data()
    if (invData.inviteeUserId) {
      alreadyInvitedUids.add(invData.inviteeUserId)
    }
  }

  // Remove already-invited users
  for (const uid of alreadyInvitedUids) {
    partnerUids.delete(uid)
  }

  if (partnerUids.size === 0) {
    return []
  }

  // 7. Resolve profile names and handles from users collection
  const partners: PartnerInfo[] = []
  const uidArray = Array.from(partnerUids)

  // Batch user lookups (Firestore 'in' limit is 30)
  for (let i = 0; i < uidArray.length; i += batchSize) {
    const batch = uidArray.slice(i, i + batchSize)
    // Use individual doc reads since we need by document ID
    for (const uid of batch) {
      const userRef = doc(db, 'users', uid)
      const userSnap = await getDoc(userRef)
      if (userSnap.exists()) {
        const userData = userSnap.data() as Omit<Database.User, 'id'>
        partners.push({
          userId: uid,
          displayName: userData.displayName || 'Unknown User',
          handle: userData.handle
        })
      }
    }
  }

  return partners
}

/**
 * Looks up a user by handle. Designed for debounce-compatible search from the UI.
 * Returns the user document or null if not found.
 */
export async function lookupUserByHandle(
  handle: string
): Promise<Database.User | null> {
  const db = getFirestore()
  const usersRef = collection(db, 'users')

  const q = query(usersRef, where('handle', '==', handle), firestoreLimit(1))

  const snap = await getDocs(q)

  if (snap.empty) {
    return null
  }

  const userDoc = snap.docs[0]
  return {
    id: userDoc.id,
    ...(userDoc.data() as Omit<Database.User, 'id'>)
  }
}
